import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, PlusIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useStore } from '../contexts/StoreContext';
import { Gallery } from '../components/vehicle/Gallery';
import { AvailabilityBadge } from '../components/AvailabilityBadge';
import { OfferStatus } from '../components/OfferStatus';
import { FinanceConditions } from '../components/FinanceConditions';
import { CircularTable } from '../components/simulator/CircularTable';
import { eligibleLines, managementFeeText } from '../utils/circular';
import { InquiryForm } from '../components/InquiryForm';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { getQuickQuote } from '../utils/finance';
import { formatARS, formatDate, formatPct } from '../utils/format';
function Spec({
  label,
  value
}: {
  label: string;
  value: string | null;
}) {
  return <div className="py-2.5 sm:grid sm:grid-cols-[180px_1fr] sm:gap-4" data-id="element-1397" data-mp="2hr015un2zzvx">
      <dt className="text-sm text-mist" data-id="element-1398" data-mp="2lf6bvepwt58v">{label}</dt>
      <dd className={`text-sm ${value ? 'text-white' : 'italic text-mist/70'}`} data-id="element-1399" data-mp="2lhsklhojdqso">{value ?? 'No disponible'}</dd>
    </div>;
}
function ListBlock({
  items
}: {
  items: string[];
}) {
  if (!items.length) return <p className="text-sm italic text-mist/70" data-id="element-1400" data-mp="2vbrha15ucid8">No disponible</p>;
  return <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1401" data-mp="2qfu1px2ejg1i">
      {items.map(i => <li key={i} className="flex items-start gap-2 text-sm" data-id="element-1402" data-mp="22q84vg9i5cwo">
          <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-neon" aria-hidden data-id="element-1403" data-mp="1xqn36y2y6qg9" />
          {i}
        </li>)}
    </ul>;
}
export function VehicleDetail() {
  const {
    id
  } = useParams();
  const {
    vehicles,
    brands,
    compareIds,
    toggleCompare,
    catalogSync
  } = useStore();
  const vehicle = vehicles.find(v => v.id === id && v.published);
  const quote = useMemo(() => vehicle ? getQuickQuote(vehicle, brands) : null, [vehicle, brands]);
  if (!vehicle && catalogSync.status === 'loading') {
    return <div className="container-x py-24 text-center" role="status" data-id="element-1404" data-mp="3f2wvijwdgg5t">
        <p className="font-display text-xl font-bold" data-id="element-1405" data-mp="3k185cumo2qo1">Cargando unidad…</p>
        <p className="mt-2 text-sm text-mist" data-id="element-1406" data-mp="3jz3rpkyyudak">Estamos consultando el inventario actualizado.</p>
      </div>;
  }
  if (!vehicle) {
    return <div className="container-x py-24 text-center" data-id="element-1407" data-mp="3strvt7st1oxs">
        <h1 className="font-display text-2xl font-bold" data-id="element-1408" data-mp="3svmm71mui999">Unidad no disponible</h1>
        <p className="mt-2 text-mist" data-id="element-1409" data-mp="2w53slhlr068f">Puede que haya sido dada de baja o que el enlace sea incorrecto.</p>
        <Link to="/catalogo" className="btn btn-primary mt-6" data-id="element-1410" data-mp="312m3ven8mxjl">Volver al catálogo</Link>
      </div>;
  }
  const brand = brands.find(b => b.id === vehicle.brandId);
  const title = `${brand?.name ?? ''} ${vehicle.model}`;
  const inCompare = compareIds.includes(vehicle.id);
  return <div className="container-x py-6 lg:py-10" data-id="element-1411" data-mp="kdgmy7exmje3">
      <Link to="/catalogo" className="inline-flex items-center gap-1.5 text-sm text-mist hover:text-white" data-id="element-1412" data-mp="pca2049vaigd">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden data-id="element-1413" data-mp="zgzvrmv4ag9i" /> Catálogo
      </Link>

      <div className="mt-4 grid gap-8 lg:grid-cols-[1fr_400px]" data-id="element-1414" data-mp="uhx1mxgodugb">
        <div className="min-w-0" data-id="element-1415" data-mp="uif44btxl4wo">
          <Gallery images={vehicle.images} alt={title} data-id="element-1416" data-mp="pqorfz7iu523" />

          <div className="mt-6 flex flex-wrap items-center gap-2" data-id="element-1417" data-mp="pp3xobt16q5a">
            <AvailabilityBadge value={vehicle.availability} data-id="element-1418" data-mp="loa9dhbof9bv" />
            <span className="text-xs text-mist" data-id="element-1419" data-mp="gq8s91xt9k31">{vehicle.segment} · {vehicle.bodyType} · Año/modelo {vehicle.year}</span>
          </div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl" data-id="element-1420" data-mp="bq48n31lw98l">{title}</h1>
          <p className="text-lg text-mist" data-id="element-1421" data-mp="6t3zukddgsds">{vehicle.version}</p>

          <section className="mt-10" aria-labelledby="s-motor" data-id="element-1422" data-mp="2qqwg2m3l6k6">
            <h2 id="s-motor" className="font-display text-lg font-bold" data-id="element-1423" data-mp="2sacpt7r7hly">Motorización</h2>
            <dl className="mt-2 divide-y divide-line" data-id="element-1424" data-mp="215k6hma2y8j6">
              <Spec label="Motor" value={vehicle.engine} data-id="element-1425" data-mp="1x3yxwd4kwges" />
              <Spec label="Transmisión" value={vehicle.transmission} data-id="element-1426" data-mp="1x2dzudj22fc6" />
              <Spec label="Consumo" value={vehicle.consumption} data-id="element-1427" data-mp="24900uf33kmqp" />
            </dl>
          </section>

          <section className="mt-10" aria-labelledby="s-seg" data-id="element-1428" data-mp="295gm91iwwn03">
            <h2 id="s-seg" className="mb-3 font-display text-lg font-bold" data-id="element-1429" data-mp="1v7jpo54gkglf">Seguridad</h2>
            <ListBlock items={vehicle.safety} data-id="element-1430" data-mp="1z8bzp0l5mpir" />
          </section>

          <section className="mt-10" aria-labelledby="s-eq" data-id="element-1431" data-mp="1zaosk8sgakal">
            <h2 id="s-eq" className="mb-3 font-display text-lg font-bold" data-id="element-1432" data-mp="1lg5v8brd66z5">Equipamiento</h2>
            <ListBlock items={vehicle.equipment} data-id="element-1433" data-mp="1q872fj9od7ib" />
          </section>

          <section className="mt-10" aria-labelledby="s-gar" data-id="element-1434" data-mp="1bj14rzqhjpcj">
            <h2 id="s-gar" className="font-display text-lg font-bold" data-id="element-1435" data-mp="1bkughtfygzre">Garantía y disponibilidad</h2>
            <dl className="mt-2 divide-y divide-line" data-id="element-1436" data-mp="1gidrmkhcwwf4">
              <Spec label="Garantía" value={vehicle.warranty} data-id="element-1437" data-mp="12i30rhg1rhty" />
              <Spec label="Disponibilidad" value={vehicle.availability} data-id="element-1438" data-mp="12kpa9mmi07et" />
              <Spec label="Vigencia de la oferta" value={quote ? `Hasta el ${formatDate(quote.ctx.offer.validUntil)}` : null} data-id="element-1439" data-mp="17gmp1oi455ph" />
              <Spec label="Datos actualizados" value={formatDate(vehicle.updatedAt)} data-id="element-1440" data-mp="iwjanrhl6bsw" />
            </dl>
          </section>

          {quote && <section className="mt-10" aria-labelledby="s-fin" data-id="element-1441" data-mp="wxdowqrbf6zh">
              <h2 id="s-fin" className="font-display text-lg font-bold" data-id="element-1442" data-mp="suz1dtcpbj1v">Condiciones financieras</h2>
              <div className="mt-2" data-id="element-1443" data-mp="16u8l33048val">
                <OfferStatus offer={quote.ctx.offer} source={quote.ctx.source} data-id="element-1444" data-mp="16t75qmc2hmn2" />
              </div>
              <div className="mt-3" data-id="element-1445" data-mp="11um72rcgrb0q">
                {quote.ctx.circular ? <>
                    <p className="mb-3 text-sm text-mist" data-id="element-1446" data-mp="1gj09j15ywxgr">
                      {quote.ctx.circular.circular.lender} · {quote.ctx.circular.circular.reference}. Cuotas con IVA incluido, sin seguro automotor. {managementFeeText(quote.ctx.circular.circular)}
                    </p>
                    <CircularTable circular={quote.ctx.circular.circular} lines={eligibleLines(quote.ctx.circular.circular, vehicle)} selected={{
                lineId: quote.ctx.circular.line.id,
                term: quote.ctx.circular.row.term
              }} data-id="element-1447" data-mp="1kmy6b57tlw6n" />
                  </> : <FinanceConditions offer={quote.ctx.offer} source={quote.ctx.source} data-id="element-1448" data-mp="r7c9dn9i6zi4" />}
              </div>
            </section>}
        </div>

        <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start" data-id="element-1449" data-mp="10ygnylv9oicf">
          <div className="card p-5" data-id="element-1450" data-mp="15xa30iq7chep">
            <p className="text-xs text-mist" data-id="element-1451" data-mp="15w8hfkbk8ciq">Precio de contado</p>
            <p className="font-display text-3xl font-black tracking-tight" data-id="element-1452" data-mp="1ay7jmqvkd3e3">{formatARS(vehicle.price)}</p>
            {quote?.sim && <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-line pt-4 text-sm" data-id="element-1453" data-mp="1ewneti1sti2l">
                <div data-id="element-1454" data-mp="3cqc5plqdt6gw"><dt className="text-[11px] text-mist" data-id="element-1455" data-mp="3cqlz7jamxvrc">Anticipo requerido</dt><dd className="font-semibold" data-id="element-1456" data-mp="3gr5v6jjh2tbw">{formatARS(quote.down)}</dd></div>
                <div data-id="element-1457" data-mp="39752adun8ncx"><dt className="text-[11px] text-mist" data-id="element-1458" data-mp="39753gh6dqtei">Capital a financiar</dt><dd className="font-semibold" data-id="element-1459" data-mp="398of1uh3cg9y">{formatARS(quote.principal)}</dd></div>
                <div data-id="element-1460" data-mp="34ekbll71aqdm"><dt className="text-[11px] text-mist" data-id="element-1461" data-mp="34dsdo3pysgk6">Cuotas estimadas</dt><dd className="font-semibold text-neon" data-id="element-1462" data-mp="2zg0k759o8mdv">{quote.term} × {formatARS(quote.sim.firstInstallment)}</dd></div>
                <div data-id="element-1463" data-mp="2vgrucbhr58nl"><dt className="text-[11px] text-mist" data-id="element-1464" data-mp="2vgruqclnzao4">TNA / CFT</dt><dd className="font-semibold" data-id="element-1465" data-mp="3s57nehk2m05g">{formatPct(quote.ctx.offer.tna)} / {formatPct(quote.ctx.offer.cft, 'N/I')}</dd></div>
              </dl>}
            {quote && <div className="mt-3" data-id="element-1466" data-mp="3n4jutrg71b94"><OfferStatus offer={quote.ctx.offer} source={quote.ctx.source} compact data-id="element-1467" data-mp="3j4j53ann9dmr" /></div>}
            <div className="mt-5 grid gap-2" data-id="element-1468" data-mp="3e5oo8e98lz5a">
              <Link to={`/simulador?v=${vehicle.id}`} className="btn btn-primary" data-id="element-1469" data-mp="3e4wtt6rdm7gl">SIMULAR FINANCIACIÓN</Link>
              <WhatsAppButton vehicle={vehicle} data-id="element-1470" data-mp="27r789jqaxva3" />
              <button onClick={() => {
              const r = toggleCompare(vehicle.id);
              if (r === 'full') toast.error('Podés comparar hasta 3 vehículos.');
            }} className="btn btn-ghost" aria-pressed={inCompare} data-id="element-1471" data-mp="22qsxvv18a98w">
                {inCompare ? <CheckIcon className="h-4 w-4" data-id="element-1472" data-mp="3svmr6nyf2fmg" /> : <PlusIcon className="h-4 w-4" data-id="element-1473" data-mp="3swpigwznik2w" />}
                {inCompare ? 'En el comparador' : 'Agregar al comparador'}
              </button>
            </div>
          </div>

          <div className="card p-5" data-id="element-1474" data-mp="peed8f6t7zx0">
            <h2 className="font-display text-lg font-bold" data-id="element-1475" data-mp="pg8pz67xglq0">Consultar por esta unidad</h2>
            <p className="mb-4 mt-1 text-sm text-mist" data-id="element-1476" data-mp="uf25132v4ksa">Un asesor comercial te responde con disponibilidad y condiciones actualizadas.</p>
            <InquiryForm channel="vehiculo" vehicle={vehicle} allowQuote idPrefix="vd" data-id="element-1477" data-mp="kmmhlq8sr400" />
          </div>
        </aside>
      </div>
    </div>;
}